import { AdminMenuPrivileges } from "../privilegesProviders/AdminMenuPrivilegesProvider";
import {
  DEFAULT_NAVIGATION_TRANSLATION_LABELS,
  type NavigationTranslationLabels,
} from "./translationLabels";
import { NavigationItem, NavigationMenu, NavigationSection } from "./types";
import { getRouteAndSelected } from "./utils";

export function getAdminNavigationMenu(
  adminMenuPrivileges: AdminMenuPrivileges,
  currentPath: string,
  translationLabels: NavigationTranslationLabels = DEFAULT_NAVIGATION_TRANSLATION_LABELS
): NavigationMenu {
  const items: Array<NavigationItem | NavigationSection> = [];

  if (adminMenuPrivileges.accountsSection) {
    const sectionItems: NavigationItem[] = [];

    if (adminMenuPrivileges.accountsSection.hcps) {
      sectionItems.push({
        label: translationLabels.workers,
        iconType: "person-male-formal",
        ...getRouteAndSelected("adminAccountsAgents", currentPath),
      });
    }
    if (adminMenuPrivileges.accountsSection.workplaces) {
      sectionItems.push({
        label: translationLabels.workplaces,
        iconType: "building-thick",
        ...getRouteAndSelected("adminAccountsFacilities", currentPath),
      });
    }
    if (adminMenuPrivileges.accountsSection.employees) {
      sectionItems.push({
        label: translationLabels.employees,
        iconType: "people",
        ...getRouteAndSelected("adminAccountsEmployees", currentPath),
      });
    }
    if (adminMenuPrivileges.accountsSection.requirements) {
      sectionItems.push({
        label: translationLabels.requirements,
        iconType: "notepad-alt",
        ...getRouteAndSelected("adminAccountsRequirement", currentPath),
      });
    }
    if (adminMenuPrivileges.accountsSection.locations) {
      sectionItems.push({
        label: translationLabels.locations,
        iconType: "location-alt",
        ...getRouteAndSelected("adminAccountsLocations", currentPath),
      });
    }
    if (adminMenuPrivileges.accountsSection.changeLog) {
      sectionItems.push({
        label: translationLabels.changeLog,
        iconType: "stack-vertical",
        ...getRouteAndSelected("adminAccountsChangeLog", currentPath),
      });
    }
    if (adminMenuPrivileges.accountsSection.dnrs) {
      sectionItems.push({
        label: translationLabels.dnrs,
        iconType: "padlock-locked",
        ...getRouteAndSelected("adminAccountsExclusions", currentPath),
      });
    }

    items.push({ label: translationLabels.accounts, items: sectionItems });
  }

  if (adminMenuPrivileges.shiftsSection) {
    const sectionItems: NavigationItem[] = [];

    if (adminMenuPrivileges.shiftsSection.shiftSearch) {
      sectionItems.push({
        label: translationLabels.shiftSearch,
        iconType: "magnifying-glass",
        ...getRouteAndSelected("adminShifts", currentPath),
      });
    }
    if (adminMenuPrivileges.shiftsSection.shiftsVerification) {
      sectionItems.push({
        label: translationLabels.shiftsVerification,
        iconType: "badge-check",
        ...getRouteAndSelected("adminShiftVerification", currentPath),
      });
    }
    if (adminMenuPrivileges.shiftsSection.shiftLogs) {
      sectionItems.push({
        label: translationLabels.shiftLogs,
        iconType: "folder-view",
        ...getRouteAndSelected("adminLogsShift", currentPath),
      });
    }
    if (adminMenuPrivileges.shiftsSection.timeclockRequests) {
      sectionItems.push({
        label: translationLabels.timeclockRequests,
        iconType: "clock",
        ...getRouteAndSelected("adminTimeclock", currentPath),
      });
    }
    if (adminMenuPrivileges.shiftsSection.noShows) {
      sectionItems.push({
        label: translationLabels.noShows,
        iconType: "gps-disabled",
        ...getRouteAndSelected("adminUrgentShiftNcns", currentPath),
      });
    }

    items.push({ label: translationLabels.shifts, items: sectionItems });
  }

  if (adminMenuPrivileges.invoiceSection) {
    const sectionItems: NavigationItem[] = [];

    if (adminMenuPrivileges.invoiceSection.byPeriod) {
      sectionItems.push({
        label: translationLabels.byPeriod,
        iconType: "centered-circle",
        ...getRouteAndSelected("adminAccountsInvocingByPeriod", currentPath),
      });
    }
    if (adminMenuPrivileges.invoiceSection.byNumber) {
      sectionItems.push({
        label: translationLabels.byNumber,
        iconType: "dash",
        ...getRouteAndSelected("adminAccountsInvocingByNumber", currentPath),
      });
    }
    if (adminMenuPrivileges.invoiceSection.postInvoiceDisputes) {
      sectionItems.push({
        label: translationLabels.postInvoiceDisputes,
        iconType: "caret-double-right",
        ...getRouteAndSelected("adminAccountsPostInvoiceDisputes", currentPath),
      });
    }
    if (adminMenuPrivileges.invoiceSection.preInvoiceDisputes) {
      sectionItems.push({
        label: translationLabels.preInvoiceDisputes,
        iconType: "caret-double-left",
        ...getRouteAndSelected("adminAccountsPreInvoiceDisputes", currentPath),
      });
    }

    items.push({ label: translationLabels.invoice, items: sectionItems });
  }

  if (adminMenuPrivileges.payrollSection) {
    const sectionItems: NavigationItem[] = [];

    if (adminMenuPrivileges.payrollSection.proofOfEarning) {
      sectionItems.push({
        label: translationLabels.payroll,
        iconType: "money-dollar",
        ...getRouteAndSelected("agentProof", currentPath),
      });
    }
    if (adminMenuPrivileges.payrollSection.shiftPayments) {
      sectionItems.push({
        label: translationLabels.shiftPayments,
        iconType: "palm-dollar",
        ...getRouteAndSelected("adminShiftPaymentsPayRates", currentPath),
      });
    }

    items.push({ label: translationLabels.payroll, items: sectionItems });
  }

  if (adminMenuPrivileges.adminToolsSection) {
    const sectionItems: NavigationItem[] = [];

    if (adminMenuPrivileges.adminToolsSection.timekeepingVideos) {
      sectionItems.push({
        label: translationLabels.timekeepingVideos,
        iconType: "replay",
        ...getRouteAndSelected("adminToolsTimekeepingVideos", currentPath),
      });
    }
    if (adminMenuPrivileges.adminToolsSection.shiftExplainer) {
      sectionItems.push({
        label: translationLabels.shiftExplainer,
        iconType: "information-circle",
        ...getRouteAndSelected("adminToolsShiftExplainer", currentPath),
      });
    }
    if (adminMenuPrivileges.adminToolsSection.badgeVerificationGrading) {
      sectionItems.push({
        label: translationLabels.badgeVerificationGrading,
        iconType: "badge-check",
        ...getRouteAndSelected("adminToolsBadgeVerificationGrading", currentPath),
      });
    }
    if (adminMenuPrivileges.adminToolsSection.identityVerification) {
      sectionItems.push({
        label: translationLabels.identityVerification,
        iconType: "person-male-formal",
        ...getRouteAndSelected("adminToolsIdentityVerification", currentPath),
      });
    }
    if (adminMenuPrivileges.adminToolsSection.badgeStrikeReviews) {
      sectionItems.push({
        label: translationLabels.badgeStrikeReviews,
        iconType: "medal-star-alt",
        ...getRouteAndSelected("adminToolsBadgeStrikeReviews", currentPath),
      });
    }
    if (adminMenuPrivileges.adminToolsSection.ssnWorkerLookup) {
      sectionItems.push({
        label: translationLabels.ssnWorkerLookup,
        iconType: "magnifying-glass",
        ...getRouteAndSelected("adminToolsSsnWorkerLookup", currentPath),
      });
    }
    if (adminMenuPrivileges.adminToolsSection.workerEvidences) {
      sectionItems.push({
        label: translationLabels.workerEvidences,
        iconType: "documents",
        ...getRouteAndSelected("adminToolsEvidences", currentPath),
      });
    }
    if (adminMenuPrivileges.adminToolsSection.eligibility) {
      sectionItems.push({
        label: translationLabels.eligibility,
        iconType: "documents",
        ...getRouteAndSelected("adminToolsEligibility", currentPath),
      });
    }
    if (adminMenuPrivileges.adminToolsSection.homeHealthWorkerLookup) {
      sectionItems.push({
        label: translationLabels.homeHealthWorkerSearch,
        iconType: "magnifying-glass",
        ...getRouteAndSelected("adminToolsHomeHealthWorkerLookup", currentPath),
      });
    }
    if (adminMenuPrivileges.adminToolsSection.bulkWorkplaceAgreementOps) {
      sectionItems.push({
        label: translationLabels.bulkWorkplaceAgreementOps,
        iconType: "documents",
        ...getRouteAndSelected("adminToolsBulkWorkplaceAgreementOps", currentPath),
      });
    }

    items.push({ label: translationLabels.adminTools, items: sectionItems });
  }

  if (adminMenuPrivileges.staffingSection) {
    const sectionItems: NavigationItem[] = [];

    if (adminMenuPrivileges.staffingSection.qualifications) {
      sectionItems.push({
        label: translationLabels.qualifications,
        iconType: "medal-star-alt",
        ...getRouteAndSelected("adminManageQualifications", currentPath),
      });
    }
    if (adminMenuPrivileges.staffingSection.workplaceTypes) {
      sectionItems.push({
        label: translationLabels.workplaceTypes,
        iconType: "house-building",
        ...getRouteAndSelected("adminManageWorkplaceTypes", currentPath),
      });
    }

    items.push({ label: translationLabels.staffing, items: sectionItems });
  }

  if (adminMenuPrivileges.hiringSection) {
    const sectionItems: NavigationItem[] = [];

    if (adminMenuPrivileges.hiringSection.availability) {
      sectionItems.push({
        label: translationLabels.availability,
        iconType: "clock",
        ...getRouteAndSelected("adminPlacementsAvailability", currentPath),
      });
    }

    if (adminMenuPrivileges.hiringSection.bans) {
      sectionItems.push({
        label: translationLabels.bans,
        iconType: "notepad-problem",
        ...getRouteAndSelected("adminAccountsPlacementCandidateBans", currentPath),
      });
    }

    if (adminMenuPrivileges.hiringSection.interviews) {
      sectionItems.push({
        label: translationLabels.interviews,
        iconType: "calendar",
        ...getRouteAndSelected("adminAccountsInterviews", currentPath),
      });
    }

    if (adminMenuPrivileges.hiringSection.scraping) {
      sectionItems.push({
        label: translationLabels.scraping,
        iconType: "route",
        ...getRouteAndSelected("adminHiringScraping", currentPath),
      });
    }

    if (adminMenuPrivileges.hiringSection.smsPortal) {
      sectionItems.push({
        label: translationLabels.smsPortal,
        iconType: "message",
        ...getRouteAndSelected("adminSmsPortal", currentPath),
      });
    }

    if (adminMenuPrivileges.hiringSection.commentReviews) {
      sectionItems.push({
        label: translationLabels.reviewsModeration,
        iconType: "notepad-problem",
        ...getRouteAndSelected("adminAccountsCommentReviews", currentPath),
      });
    }

    if (adminMenuPrivileges.hiringSection.externalWorkplacePhotos) {
      sectionItems.push({
        label: translationLabels.externalPhotos,
        iconType: "binoculars",
        ...getRouteAndSelected("adminAccountsExternalWorkplacePhotosScraping", currentPath),
      });
    }

    items.push({ label: translationLabels.hiring, items: sectionItems });
  }

  return { items };
}
